import React, { useState } from "react";
import { FiUser, FiBriefcase, FiMail, FiPhone, FiMapPin, FiEdit2, FiTrash2, FiShoppingBag, FiDollarSign, FiSearch } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import { useCreateCustomerMutation, useDeleteCustomerMutation, useGetCustomersQuery, useUpdateCustomerMutation } from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import { money } from "../utils/formatters";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  company_name: "",
  address: "",
  customer_type: "business",
};

const CustomersPage = () => {
  const { data } = useGetCustomersQuery();
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateCustomer({ id: editingId, ...form }).unwrap();
      } else {
        await createCustomer(form).unwrap();
      }
      setEditingId(null);
      setForm(emptyForm);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to save customer."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCustomers = data?.data?.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const getCustomerTypeColor = (type) => {
    switch (type) {
      case "business":
        return "#3b82f6";
      case "corporate":
        return "#8b5cf6";
      case "reseller":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="dashboard-page">
      <SectionHeader eyebrow="Customer Management" title="Manage customer profiles, orders, and relationships" />
      
      <div className="dashboard-grid dashboard-grid--wide">
        {/* Customer Directory */}
        <section className="panel">
          <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--border)" }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.25rem", fontWeight: "700" }}>Customer Directory</h3>
            
            {/* Search Bar */}
            <div className="form-input-group" style={{ maxWidth: "400px" }}>
              <span className="form-input-group__icon"><FiSearch /></span>
              <input 
                className="form-input" 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                placeholder="Search customers..." 
              />
            </div>
          </div>

          {/* Customer Cards Grid */}
          <div className="gallery-admin-grid">
            {filteredCustomers.length === 0 ? (
              <div className="empty-state-modern" style={{ gridColumn: "1 / -1" }}>
                <FiUser />
                <p>{searchQuery ? "No customers found" : "No customers yet"}</p>
              </div>
            ) : (
              filteredCustomers.map((customer) => (
                <div key={customer.id} className="gallery-admin-card">
                  {/* Customer Avatar Header */}
                  <div 
                    className="gallery-admin-card__image-wrapper" 
                    style={{ 
                      background: `linear-gradient(135deg, ${getCustomerTypeColor(customer.customer_type)}22 0%, ${getCustomerTypeColor(customer.customer_type)}44 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative"
                    }}
                  >
                    <div 
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${getCustomerTypeColor(customer.customer_type)} 0%, ${getCustomerTypeColor(customer.customer_type)}dd 100%)`,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        fontWeight: "800",
                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)"
                      }}
                    >
                      {getInitials(customer.name)}
                    </div>
                    
                    {/* Customer Type Badge */}
                    <div 
                      className="gallery-admin-card__badge"
                      style={{ background: `${getCustomerTypeColor(customer.customer_type)}ee` }}
                    >
                      <FiBriefcase size={12} />
                      {customer.customer_type}
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="gallery-admin-card__content">
                    <h4 className="gallery-admin-card__title">{customer.name}</h4>
                    
                    {customer.company_name && (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "0.5rem", fontSize: "0.875rem", color: "var(--text-muted)" }}>
                        <FiBriefcase size={14} />
                        <span>{customer.company_name}</span>
                      </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "0.25rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                      <FiMail size={14} />
                      <span>{customer.email}</span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "0.75rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                      <FiPhone size={14} />
                      <span>{customer.phone}</span>
                    </div>

                    {customer.address && (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", marginBottom: "0.75rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                        <FiMapPin size={14} style={{ marginTop: "2px", flexShrink: 0 }} />
                        <span style={{ lineHeight: "1.5" }}>{customer.address}</span>
                      </div>
                    )}

                    {/* Stats */}
                    <div style={{ 
                      display: "flex", 
                      gap: "1rem", 
                      padding: "0.75rem", 
                      background: "var(--bg-subtle)", 
                      borderRadius: "8px",
                      marginBottom: "0.75rem"
                    }}>
                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "4px" }}>
                          <FiShoppingBag size={12} />
                          Orders
                        </div>
                        <div style={{ fontSize: "1.25rem", fontWeight: "800", color: "var(--brand)" }}>{customer.total_orders || 0}</div>
                      </div>
                      <div style={{ width: "1px", background: "var(--border)" }}></div>
                      <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "4px" }}>
                          <FiDollarSign size={12} />
                          Total Spend
                        </div>
                        <div style={{ fontSize: "1.25rem", fontWeight: "800", color: "#22c55e" }}>{money(customer.total_spend || 0)}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="gallery-admin-card__actions">
                      <button 
                        className="btn-icon btn-icon--edit" 
                        type="button" 
                        onClick={() => { 
                          setEditingId(customer.id); 
                          setForm(customer); 
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        <FiEdit2 size={16} />
                        Edit
                      </button>
                      <button 
                        className="btn-icon btn-icon--delete" 
                        type="button" 
                        onClick={() => {
                          if (window.confirm(`Delete customer "${customer.name}"?`)) {
                            deleteCustomer(customer.id);
                          }
                        }}
                      >
                        <FiTrash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Customer Form */}
        <div className="panel" style={{ position: "sticky", top: "1rem", alignSelf: "flex-start" }}>
          <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
            {/* Form Header */}
            <div style={{ marginBottom: "1.5rem" }}>
              <h3 style={{ 
                margin: "0 0 0.5rem 0", 
                fontSize: "1.25rem", 
                fontWeight: "700",
                color: "var(--text-primary)"
              }}>
                {editingId ? "Edit Customer" : "Add New Customer"}
              </h3>
              <p style={{ 
                margin: "0", 
                fontSize: "0.875rem", 
                color: "var(--text-muted)" 
              }}>
                {editingId ? "Update customer information" : "Create a new customer profile"}
              </p>
            </div>

            {submitError && (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.875rem 1rem",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                color: "#dc2626",
                fontSize: "0.875rem",
                marginBottom: "1.5rem"
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: "20px", height: "20px", flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{submitError}</span>
              </div>
            )}

            {/* Form Fields */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {/* Full Name */}
              <div>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "0.875rem", 
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  marginBottom: "0.5rem"
                }}>
                  <FiUser size={14} />
                  Full Name
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiUser /></span>
                  <input 
                    className="form-input" 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                    placeholder="Enter customer full name" 
                    required
                    style={{ 
                      padding: "0.75rem 0.75rem 0.75rem 2.75rem",
                      fontSize: "0.9375rem"
                    }}
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "0.875rem", 
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  marginBottom: "0.5rem"
                }}>
                  <FiBriefcase size={14} />
                  Company Name
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiBriefcase /></span>
                  <input 
                    className="form-input" 
                    value={form.company_name} 
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })} 
                    placeholder="Enter company name (optional)" 
                    style={{ 
                      padding: "0.75rem 0.75rem 0.75rem 2.75rem",
                      fontSize: "0.9375rem"
                    }}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "0.875rem", 
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  marginBottom: "0.5rem"
                }}>
                  <FiMail size={14} />
                  Email Address
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiMail /></span>
                  <input 
                    className="form-input" 
                    type="email"
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                    placeholder="customer@example.com" 
                    required
                    style={{ 
                      padding: "0.75rem 0.75rem 0.75rem 2.75rem",
                      fontSize: "0.9375rem"
                    }}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "0.875rem", 
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  marginBottom: "0.5rem"
                }}>
                  <FiPhone size={14} />
                  Phone Number
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div className="form-input-group">
                  <span className="form-input-group__icon"><FiPhone /></span>
                  <input 
                    className="form-input" 
                    type="tel"
                    value={form.phone} 
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                    placeholder="+251 9XX XXX XXX" 
                    required
                    style={{ 
                      padding: "0.75rem 0.75rem 0.75rem 2.75rem",
                      fontSize: "0.9375rem"
                    }}
                  />
                </div>
              </div>

              {/* Customer Type */}
              <div>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "0.875rem", 
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  marginBottom: "0.5rem"
                }}>
                  <FiBriefcase size={14} />
                  Customer Type
                </label>
                <select 
                  className="form-select" 
                  value={form.customer_type} 
                  onChange={(e) => setForm({ ...form, customer_type: e.target.value })}
                  style={{ 
                    padding: "0.75rem",
                    fontSize: "0.9375rem",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    background: "var(--bg-base)",
                    color: "var(--text-primary)",
                    width: "100%",
                    cursor: "pointer"
                  }}
                >
                  <option value="individual">👤 Individual</option>
                  <option value="business">💼 Business</option>
                  <option value="corporate">🏢 Corporate</option>
                  <option value="reseller">🔄 Reseller</option>
                </select>
              </div>

              {/* Address */}
              <div>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "6px", 
                  fontSize: "0.875rem", 
                  fontWeight: "600",
                  color: "var(--text-primary)",
                  marginBottom: "0.5rem"
                }}>
                  <FiMapPin size={14} />
                  Address
                </label>
                <textarea 
                  className="form-textarea" 
                  value={form.address} 
                  onChange={(e) => setForm({ ...form, address: e.target.value })} 
                  placeholder="Enter customer address (optional)" 
                  rows="3"
                  style={{ 
                    padding: "0.75rem",
                    fontSize: "0.9375rem",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    background: "var(--bg-base)",
                    color: "var(--text-primary)",
                    width: "100%",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: "1.5"
                  }}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ 
              display: "flex", 
              gap: "0.75rem", 
              marginTop: "1.5rem",
              paddingTop: "1.5rem",
              borderTop: "1px solid var(--border)"
            }}>
              {editingId && (
                <button 
                  className="btn-secondary" 
                  type="button" 
                  onClick={() => { 
                    setEditingId(null); 
                    setForm(emptyForm); 
                    setSubmitError(""); 
                  }}
                  style={{
                    flex: 1,
                    padding: "0.875rem 1.5rem",
                    fontSize: "0.9375rem",
                    fontWeight: "600",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    background: "var(--bg-base)",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  Cancel
                </button>
              )}
              <button 
                className="btn-primary" 
                type="submit"
                disabled={isSubmitting}
                style={{
                  flex: editingId ? 1 : "auto",
                  width: editingId ? "auto" : "100%",
                  padding: "0.875rem 1.5rem",
                  fontSize: "0.9375rem",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: "none",
                  background: isSubmitting ? "#9ca3af" : "linear-gradient(135deg, #0F5D4F 0%, #22A06B 100%)",
                  color: "#fff",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem"
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      animation: "spin 0.6s linear infinite"
                    }}></div>
                    Saving...
                  </>
                ) : (
                  editingId ? "Update Customer" : "Save Customer"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;
